import { Grid } from '@mui/material'
import axios from 'axios'
import { Bin } from 'chezy-inventory-backend/bin/database/types/Bin'
import { Item } from 'chezy-inventory-backend/bin/database/types/Item'
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { getBaseURL } from '../../utils/getBaseURL'
import { usePrevious } from '../../utils/usePrevious'

import { NoBinViewer } from './noBinSelectedViewer'
import { BinBrowserSidebar } from './sidebar'
import { BinViewer } from './viewer'

const getBins = async () => {
  return (await axios.get(`${getBaseURL()}/bins`)).data as Bin[]
}

const getItems = async (binID: string) => {
  return (await axios.get(`${getBaseURL()}/bin/${binID}/items`)).data as Item[]
}

const BinBrowser = () => {
  const { binId: paramBinId } = useParams()
  const [bins, setBins] = useState<Bin[]>()
  const [activeBin, setActiveBin] = useState<Bin>()
  const prevActiveBin = usePrevious(activeBin)
  const [prevParamBinId, setPrevParamBinId] = useState<string>()

  const [items, setItems] = useState<Item[]>()

  useEffect(() => {
    getBins().then((bins) => {
      setBins(bins)
    })
  }, [])

  const onReload = useCallback(() => {
    getBins().then((bins) => {
      setBins(bins)
    })
    if (activeBin) {
      getItems(activeBin.id)
        .then((items) => {
          setItems(items)
        })
        .catch(console.error)
    }
  }, [activeBin])

  useEffect(() => {
    if (prevActiveBin?.id !== activeBin?.id) onReload()
  }, [activeBin, onReload, prevActiveBin])

  useEffect(() => {
    if (bins && activeBin && !bins.map((b) => b.id).includes(activeBin.id)) {
      setActiveBin(bins[0])
    }
  }, [bins, activeBin])

  useEffect(() => {
    const activeBinFound = activeBin && bins?.find((b) => b.id === activeBin.id)
    if (bins && activeBin && activeBin !== activeBinFound) {
      // reload active bin object in the even that it was changed
      setActiveBin(activeBinFound)
    }
  }, [bins, activeBin])

  const onSelect = useCallback(
    (id: string | undefined) => {
      setActiveBin(bins?.find((b) => b.id === id))
      if (bins) setPrevParamBinId(paramBinId)
    },
    [bins, paramBinId]
  )

  useEffect(() => {
    if (paramBinId !== prevParamBinId) {
      onSelect(paramBinId)
    }
  }, [paramBinId, prevParamBinId, onSelect])

  return (
    <Grid container spacing={2}>
      <Grid item xs={3}>
        <BinBrowserSidebar bins={bins ?? []} onSelect={onSelect} />
      </Grid>
      <Grid item xs={9}>
        {activeBin ? (
          <BinViewer allBins={bins ?? []} items={items ?? []} activeBin={activeBin} onReload={onReload} />
        ) : (
          <NoBinViewer onReload={onReload} allBins={bins ?? []} onSelectBin={onSelect} />
        )}
      </Grid>
    </Grid>
  )
}

export { BinBrowser }
